import React, { useState, useEffect } from "react";
import {
  Box, Button, Typography, TextField, Switch, FormControlLabel,
  Card, CardContent, Grid, Alert, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, DialogContentText, Tabs, Tab,
  Chip, IconButton, Paper, Tooltip, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { DATABASE, AUTH_LOGIN } from "../../../Config/firebase";
import { FUNCTIONS_URLS } from "../../../Config/functions";
import { adminAuthHeaders } from "../../../Config/adminAuthHeaders";
import { toast } from "react-toastify";
import SettingsIcon from "@mui/icons-material/Settings";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import BrushIcon from "@mui/icons-material/Brush";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import InfoIcon from "@mui/icons-material/Info";
import LinkIcon from "@mui/icons-material/Link";
import LinkGoogleCard from "./LinkGoogleCard";

// Default values matching current hardcoded constants in functions/index.js
const DEFAULTS = {
  // Email Controls
  emails_enabled: true,
  admin_emails_enabled: true,
  otp_emails_enabled: true,
  yield_emails_enabled: true,
  campaign_emails_enabled: true,
  testing_mode: false,
  test_emails_whitelist: ["jrmascarin@gmail.com", "peitoramusic@gmail.com", "admin@fidusfinance.com"],
  excluded_admin_emails: ["admin@equitysoft.in", "sachin@equitysoft.in", "test-admin@fidusfinance.com"],
  // Alert Thresholds
  high_value_withdrawal_threshold: 10000,
  large_deposit_threshold: 50000,
  alert_email: "admin@fidusfinance.com",
  // Branding
  company_name: "FidusFinance OÜ - 16290879",
  company_address: "Jõe tn 2a Tallinn Harjumaa 10151",
  company_email: "order@fiddux.com",
  // Monitoring
  monitoring_window_hours: 2,
  scheduled_cron_interval_minutes: 120,
  functions_to_monitor: [
    "autoCancelFullWithdrawalTransactions",
    "createWithdrawOtpVerifyNotification"
  ],
};

const FIRESTORE_PATH = "admin_settings";
const FIRESTORE_DOC = "config";

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} style={{ paddingTop: "24px" }}>
    {value === index && children}
  </div>
);

/** Readable on dark admin theme (avoid MUI text.secondary). */
const CAPTION_MUTED = "rgba(255,255,255,0.65)";
const BODY_MUTED = "rgba(255,255,255,0.72)";
const FIELD_LABEL_PROPS = { sx: { color: CAPTION_MUTED } };
const FIELD_INPUT_PROPS = { sx: { color: "#fff" } };
const FIELD_HELPER_PROPS = { sx: { color: "rgba(255,255,255,0.55)" } };

const SectionCard = ({ title, description, children }) => (
  <Card sx={{ mb: 3, bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
    <CardContent>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#fff" }}>{title}</Typography>
      {description && (
        <Typography variant="body2" sx={{ mb: 2, color: BODY_MUTED, lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      <Divider sx={{ mb: 2, borderColor: "rgba(255,255,255,0.1)" }} />
      {children}
    </CardContent>
  </Card>
);

const ASSET_BRAND = [
  { id: "USDT", label: "USDT", dot: "#26a17b", activeColor: "#2af598", activeBg: "rgba(38,161,123,0.15)", activeBorder: "rgba(42,245,152,0.4)" },
  { id: "USDC", label: "USDC", dot: "#2775ca", activeColor: "#7eb8ff", activeBg: "rgba(39,117,202,0.15)", activeBorder: "rgba(126,184,255,0.4)" },
  { id: "BTC",  label: "BTC",  dot: "#f7931a", activeColor: "#f7931a", activeBg: "rgba(247,147,26,0.12)", activeBorder: "rgba(247,147,26,0.4)" },
];

const NETWORK_BRAND = [
  { id: "POLYGON",  label: "POLYGON",  dot: "#8247e5", activeColor: "#b47cff", activeBg: "rgba(130,71,229,0.15)", activeBorder: "rgba(180,124,255,0.4)" },
  { id: "BSC",      label: "BSC",      dot: "#f3ba2f", activeColor: "#f3ba2f", activeBg: "rgba(243,186,47,0.12)", activeBorder: "rgba(243,186,47,0.4)" },
  { id: "ETHEREUM", label: "ETHEREUM", dot: "#627eea", activeColor: "#8fa8ff", activeBg: "rgba(98,126,234,0.15)", activeBorder: "rgba(143,168,255,0.4)" },
  { id: "TRON",     label: "TRON",     dot: "#eb0029", activeColor: "#ff5c70", activeBg: "rgba(235,0,41,0.12)",  activeBorder: "rgba(255,92,112,0.4)" },
];

/** Assets that require a liquidity_wallets entry when enabled in auto-pay. */
const AUTO_PAY_LIQUIDITY_ASSETS = ["USDT", "USDC"];

/** Pairs asset_network for liquidity wallet fields (cartesian of enabled toggles). */
const buildLiquidityWalletKeys = (autoPay) => {
  const assets = (autoPay?.supported_assets || []).filter((a) => AUTO_PAY_LIQUIDITY_ASSETS.includes(a));
  const networks = autoPay?.supported_networks || [];
  return assets.flatMap((asset) => networks.map((network) => `${asset}_${network}`));
};

const missingLiquidityWalletKeys = (autoPay) => {
  const liq = autoPay?.liquidity_wallets || {};
  return buildLiquidityWalletKeys(autoPay).filter((key) => !String(liq[key] || "").trim());
};

const DEFAULT_AUTO_PAY = {
  enabled: false,
  min_amount: 100,
  max_amount: 0,
  daily_cap: 0,
  accept_delay_minutes: 720,
  pay_delay_minutes: 360,
  max_window_hours: 24,
  operating_window_enabled: false,
  operating_start_hour: 9,
  operating_end_hour: 21,
  operating_timezone: "Europe/London",
  supported_assets: ["USDT", "USDC"],
  supported_networks: ["POLYGON"],
  liquidity_wallets: { USDT_POLYGON: "", USDC_POLYGON: "", USDT_BSC: "", USDC_BSC: "" },
  kyc_whitelist_uids: [],
  exchange_enabled: false,
  exchange_provider: "bybit",
};

/**
 * Illustrative operating-window scenario, computed dynamically from the current
 * config. Mirrors the cron logic in whole-hour granularity: eligibility =
 * request + delay; if that moment is outside the window, it is deferred to the
 * next window opening. Used only to render the always-visible examples table.
 */
const isHourWithinWindow = (hourOfDay, startHour, endHour) => {
  const h = ((hourOfDay % 24) + 24) % 24;
  if (startHour === endHour) return true; // degenerate → treat as 24h
  if (startHour < endHour) return h >= startHour && h < endHour;
  return h >= startHour || h < endHour; // wrap-around
};

const computeWindowScenario = ({ requestHour, startHour, endHour, acceptDelayH, payDelayH }) => {
  const nextOpen = (absHour) => {
    let cur = absHour;
    let guard = 0;
    while (!isHourWithinWindow(cur, startHour, endHour) && guard < 72) {
      cur += 1;
      guard += 1;
    }
    return cur;
  };
  const acceptEligible = requestHour + acceptDelayH;
  const acceptDeferred = !isHourWithinWindow(acceptEligible, startHour, endHour);
  const acceptActual = nextOpen(acceptEligible);
  const payEligible = acceptActual + payDelayH;
  const payDeferred = !isHourWithinWindow(payEligible, startHour, endHour);
  const payActual = nextOpen(payEligible);
  return { acceptEligible, acceptActual, acceptDeferred, payEligible, payActual, payDeferred };
};

const fmtAbsHour = (absHour) => {
  const dayOffset = Math.floor(absHour / 24);
  const hourOfDay = ((absHour % 24) + 24) % 24;
  const hh = String(hourOfDay).padStart(2, "0");
  return `${hh}:00${dayOffset > 0 ? ` (+${dayOffset}d)` : ""}`;
};

const AdminSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({ ...DEFAULTS });
  const [originalSettings, setOriginalSettings] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState(null);
  const [newWhitelistEmail, setNewWhitelistEmail] = useState("");
  const [newExcludedEmail, setNewExcludedEmail] = useState("");
  const [newFunction, setNewFunction] = useState("");

  // Auto-pay (fund_rules/active) — separate state
  const [autoPay, setAutoPay] = useState({ ...DEFAULT_AUTO_PAY });
  const [originalAutoPay, setOriginalAutoPay] = useState(null);
  const [autoPayHasChanges, setAutoPayHasChanges] = useState(false);
  const [autoPaySaving, setAutoPaySaving] = useState(false);

  const isSandbox = process.env.REACT_APP_FIREBASE_PROJECT === "fiddux-sandbox-dev";

  useEffect(() => {
    loadSettings();
    loadAutoPaySettings();
  }, []);

  useEffect(() => {
    if (originalSettings) {
      setHasChanges(JSON.stringify(settings) !== JSON.stringify(originalSettings));
    }
  }, [settings, originalSettings]);

  useEffect(() => {
    if (originalAutoPay) {
      setAutoPayHasChanges(JSON.stringify(autoPay) !== JSON.stringify(originalAutoPay));
    }
  }, [autoPay, originalAutoPay]);

  const loadAutoPaySettings = async () => {
    try {
      const snap = await getDoc(doc(DATABASE, "fund_rules", "active"));
      if (snap.exists()) {
        const ap = snap.data()?.auto_pay || {};
        const merged = { ...DEFAULT_AUTO_PAY, ...ap };
        setAutoPay(merged);
        setOriginalAutoPay(merged);
      } else {
        setAutoPay({ ...DEFAULT_AUTO_PAY });
        setOriginalAutoPay({ ...DEFAULT_AUTO_PAY });
      }
    } catch (e) {
      console.error("Failed to load auto-pay settings", e);
    }
  };

  const saveAutoPaySettings = async () => {
    const missingLiq = missingLiquidityWalletKeys(autoPay);
    if (missingLiq.length > 0) {
      toast.error(`Preencha as liquidity wallets: ${missingLiq.join(", ")}`);
      return;
    }
    setAutoPaySaving(true);
    try {
      const currentUser = AUTH_LOGIN.currentUser;
      const rulesRef = doc(DATABASE, "fund_rules", "active");
      const snap = await getDoc(rulesRef);
      const current = snap.exists() ? snap.data() : {};
      const version = current.version || "1.0";
      const parts = version.split(".");
      const newVersion = `${parts[0]}.${(parseInt(parts[1] || 0) + 1)}`;
      await setDoc(
        rulesRef,
        {
          ...current,
          auto_pay: autoPay,
          updated_at: Timestamp.now(),
          updated_by: currentUser?.email || "admin",
          version: newVersion,
        },
        { merge: true }
      );
      setOriginalAutoPay({ ...autoPay });
      setAutoPayHasChanges(false);
      toast.success("Auto-pay settings saved.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to save auto-pay settings.");
    } finally {
      setAutoPaySaving(false);
    }
  };

  const updateAutoPay = (key, value) => setAutoPay((prev) => ({ ...prev, [key]: value }));
  const updateAutoPayLiquidityWallet = (key, value) =>
    setAutoPay((prev) => ({ ...prev, liquidity_wallets: { ...prev.liquidity_wallets, [key]: value } }));
  const toggleAutoPayAsset = (asset) => {
    const current = autoPay.supported_assets || [];
    const next = current.includes(asset) ? current.filter((a) => a !== asset) : [...current, asset];
    updateAutoPay("supported_assets", next);
  };
  const toggleAutoPayNetwork = (net) => {
    const current = autoPay.supported_networks || [];
    const next = current.includes(net) ? current.filter((n) => n !== net) : [...current, net];
    updateAutoPay("supported_networks", next);
  };

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const docRef = doc(DATABASE, FIRESTORE_PATH, FIRESTORE_DOC);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const merged = { ...DEFAULTS, ...data };
        setSettings(merged);
        setOriginalSettings(merged);
        if (data.updated_at) {
          setLastUpdated(data.updated_at.toDate ? data.updated_at.toDate() : new Date(data.updated_at));
        }
        setLastUpdatedBy(data.updated_by || null);
      } else {
        setSettings({ ...DEFAULTS });
        setOriginalSettings({ ...DEFAULTS });
      }
    } catch (error) {
      console.error("Error loading admin settings:", error);
      toast.error("Failed to load admin settings.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => setConfirmOpen(true);

  const confirmSave = async () => {
    setConfirmOpen(false);
    setIsSaving(true);
    const cronIntervalChanged = originalSettings && originalSettings.scheduled_cron_interval_minutes !== settings.scheduled_cron_interval_minutes;
    try {
      const currentUser = AUTH_LOGIN.currentUser;
      const dataToSave = {
        ...settings,
        updated_at: Timestamp.now(),
        updated_by: currentUser?.email || "unknown",
      };
      const docRef = doc(DATABASE, FIRESTORE_PATH, FIRESTORE_DOC);
      await setDoc(docRef, dataToSave, { merge: true });
      if (cronIntervalChanged) {
        try {
          const res = await fetch(FUNCTIONS_URLS.updateScheduledCronInterval, {
            method: "POST",
            headers: await adminAuthHeaders(),
            body: JSON.stringify({ interval_minutes: settings.scheduled_cron_interval_minutes }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || data.details || "Failed to update cron");
          toast.success("Admin settings and cron interval updated successfully.");
        } catch (err) {
          console.error("Error updating cron interval:", err);
          toast.warning("Settings saved, but cron interval update failed: " + (err.message || "unknown error"));
        }
      } else {
        toast.success("Admin settings saved successfully.");
      }
      setOriginalSettings({ ...settings });
      setLastUpdated(new Date());
      setLastUpdatedBy(currentUser?.email || "unknown");
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving admin settings:", error);
      toast.error("Failed to save admin settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings({ ...originalSettings });
    }
  };

  const handleResetToDefaults = () => {
    setSettings({ ...DEFAULTS });
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const addToList = (key, value, clearFn) => {
    if (!value || !value.trim()) return;
    const trimmed = value.trim().toLowerCase();
    const list = settings[key] || [];
    if (list.includes(trimmed)) {
      toast.warning("Already in the list.");
      return;
    }
    updateSetting(key, [...list, trimmed]);
    clearFn("");
  };

  const removeFromList = (key, index) => {
    const list = [...(settings[key] || [])];
    list.splice(index, 1);
    updateSetting(key, list);
  };

  // ==================== TAB 1: Email Controls ====================
  const renderEmailControls = () => (
    <>
      {isSandbox && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sandbox mode: Even if emails are enabled here, IS_SANDBOX in Cloud Functions will override and block all email sending.
        </Alert>
      )}

      <SectionCard title="Email Master Controls" description="Toggle email sending on/off for different categories.">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.emails_enabled} onChange={(e) => updateSetting("emails_enabled", e.target.checked)} />}
              label="Client Emails Enabled"
            />
            <Typography variant="caption" sx={{ color: CAPTION_MUTED, display: "block" }}>
              Monthly PDFs, verification emails, deposit/withdrawal confirmations
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.admin_emails_enabled} onChange={(e) => updateSetting("admin_emails_enabled", e.target.checked)} />}
              label="Admin Emails Enabled"
            />
            <Typography variant="caption" sx={{ color: CAPTION_MUTED, display: "block" }}>
              Admin notifications for deposits, withdrawals, new accounts
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.otp_emails_enabled} onChange={(e) => updateSetting("otp_emails_enabled", e.target.checked)} />}
              label="OTP Emails Enabled"
            />
            <Typography variant="caption" sx={{ color: CAPTION_MUTED, display: "block" }}>
              OTP verification codes for withdrawals
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={settings.yield_emails_enabled} onChange={(e) => updateSetting("yield_emails_enabled", e.target.checked)} />}
              label="Yield Emails Enabled"
            />
            <Typography variant="caption" sx={{ color: CAPTION_MUTED, display: "block" }}>
              Yield product terms and final performance emails
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.campaign_emails_enabled !== false}
                  onChange={(e) => updateSetting("campaign_emails_enabled", e.target.checked)}
                />
              }
              label="Campaign Emails Enabled"
            />
            <Typography variant="caption" sx={{ color: CAPTION_MUTED, display: "block" }}>
              Client Communications / CRM Lite blasts (kill-switch dedicated)
            </Typography>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Testing Mode" description="When enabled, emails are only sent to whitelisted addresses.">
        <FormControlLabel
          control={<Switch checked={settings.testing_mode} onChange={(e) => updateSetting("testing_mode", e.target.checked)} color="warning" />}
          label="Testing Mode Active"
        />
        {settings.testing_mode && (
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            Only emails in the whitelist below will receive messages.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Email Whitelist (Testing Mode)</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {(settings.test_emails_whitelist || []).map((email, i) => (
            <Chip key={i} label={email} onDelete={() => removeFromList("test_emails_whitelist", i)} size="small" />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder="email@example.com" value={newWhitelistEmail} onChange={(e) => setNewWhitelistEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addToList("test_emails_whitelist", newWhitelistEmail, setNewWhitelistEmail)}
            sx={{ minWidth: 280 }} />
          <IconButton size="small" onClick={() => addToList("test_emails_whitelist", newWhitelistEmail, setNewWhitelistEmail)} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </SectionCard>

      <SectionCard title="Excluded Admin Emails" description="These admin emails will never receive admin notifications.">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {(settings.excluded_admin_emails || []).map((email, i) => (
            <Chip key={i} label={email} onDelete={() => removeFromList("excluded_admin_emails", i)} size="small" color="error" variant="outlined" />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder="email@example.com" value={newExcludedEmail} onChange={(e) => setNewExcludedEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addToList("excluded_admin_emails", newExcludedEmail, setNewExcludedEmail)}
            sx={{ minWidth: 280 }} />
          <IconButton size="small" onClick={() => addToList("excluded_admin_emails", newExcludedEmail, setNewExcludedEmail)} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </SectionCard>
    </>
  );

  // ==================== TAB 2: Alert Thresholds ====================
  const renderAlertThresholds = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)", width: "100%" } }}>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 600, mb: 0.5 }}>How alerts work</Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
          When a new deposit or withdrawal is created in <code>transection_requests</code>, the Cloud Functions{" "}
          <code>monitorLargeDeposits</code> and <code>monitorNewWithdrawals</code> compare the amount against these thresholds.
          If exceeded, an{" "}
          <Box component="span" sx={{ color: "#ffd54f", fontWeight: 700 }}>admin email alert is sent immediately</Box>
          {" "}to the alert email below. Thresholds are read dynamically — no re-deploy needed after saving.
        </Typography>
      </Alert>

      <SectionCard title="Transaction Alert Thresholds" description="USD amounts that trigger an immediate admin email alert on new transactions.">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="High-Value Withdrawal Threshold (USD)" type="number"
              value={settings.high_value_withdrawal_threshold}
              onChange={(e) => updateSetting("high_value_withdrawal_threshold", Number(e.target.value))}
              helperText="New withdrawal above this amount → immediate email to alert_email"
              InputLabelProps={FIELD_LABEL_PROPS}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "#2af598", fontWeight: 700 }}>$</Typography>, sx: { color: "#fff" } }}
              FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Large Deposit Threshold (USD)" type="number"
              value={settings.large_deposit_threshold}
              onChange={(e) => updateSetting("large_deposit_threshold", Number(e.target.value))}
              helperText="New deposit above this amount → immediate email to alert_email"
              InputLabelProps={FIELD_LABEL_PROPS}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "#2af598", fontWeight: 700 }}>$</Typography>, sx: { color: "#fff" } }}
              FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Alert Recipient" description="Email address that receives all system and transaction alerts.">
        <TextField fullWidth label="Alert Email" type="email"
          value={settings.alert_email}
          onChange={(e) => updateSetting("alert_email", e.target.value)}
          helperText="Receives: high-value withdrawal alerts, large deposit alerts, function error notifications"
          InputLabelProps={FIELD_LABEL_PROPS}
          InputProps={FIELD_INPUT_PROPS}
          FormHelperTextProps={FIELD_HELPER_PROPS}
          sx={{ maxWidth: 440 }}
        />
      </SectionCard>
    </>
  );

  // ==================== TAB 3: Branding ====================
  const renderBranding = () => (
    <>
      <SectionCard title="Company Information" description="Used in PDF reports, email footers, and legal documents.">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField fullWidth label="Company Name" value={settings.company_name}
              onChange={(e) => updateSetting("company_name", e.target.value)}
              helperText="Displayed in PDF headers and email templates"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Company Address" value={settings.company_address}
              onChange={(e) => updateSetting("company_address", e.target.value)}
              helperText="Displayed in PDF footers"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Email" type="email" value={settings.company_email}
              onChange={(e) => updateSetting("company_email", e.target.value)}
              helperText="Displayed in PDF footers and as reply-to in emails"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
        </Grid>
      </SectionCard>
    </>
  );

  // ==================== TAB 4: Automations (Auto-Pay) ====================
  const renderAutomations = () => (
    <>
      <Alert severity="info" sx={{ mb: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)" } }}>
        Auto-pay settings live in Firestore at <code>fund_rules/active → auto_pay</code> and are read by the Cloud Function <code>processAutoPayWithdrawal</code> every minute.
        Commission, deposit &amp; withdrawal rules are at{" "}
        <Box component="a" href="/admin/fund-rules" sx={{ color: "#7eb8ff", textDecoration: "underline", display: "inline-flex", alignItems: "center", gap: 0.25 }}>
          Fund Rules <LinkIcon sx={{ fontSize: 14 }} />
        </Box>.
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Box
          component="a"
          href="/admin/auto-pay-flow"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.25,
            py: 1.1,
            borderRadius: 2,
            bgcolor: "rgba(42,245,152,0.10)",
            border: "1px solid rgba(42,245,152,0.34)",
            color: "#2af598",
            fontWeight: 800,
            fontSize: 14,
            textDecoration: "none",
            "&:hover": { bgcolor: "rgba(42,245,152,0.18)" },
          }}
        >
          ⚡ Open detailed Auto-Pay flow (diagrams, gates &amp; live config) →
        </Box>
      </Box>

      <SectionCard
        title="Auto-Pay"
        description="When ON, eligible small withdrawals are auto-accepted and marked ready-to-pay without manual admin action."
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderRadius: 2,
            border: `2px solid ${autoPay.enabled ? "rgba(42,245,152,0.5)" : "rgba(255,255,255,0.12)"}`,
            bgcolor: autoPay.enabled ? "rgba(42,245,152,0.08)" : "rgba(255,255,255,0.03)",
            transition: "all 0.2s",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
              Auto-Pay is{" "}
              <Box
                component="span"
                sx={{ color: autoPay.enabled ? "#2af598" : "#ff8a80" }}
              >
                {autoPay.enabled ? "ON" : "OFF"}
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ color: CAPTION_MUTED, mt: 0.5 }}>
              {autoPay.enabled
                ? "Active — eligible withdrawals will be processed automatically."
                : "Paused — all withdrawals require manual admin approval."}
            </Typography>
          </Box>
          <Switch
            checked={!!autoPay.enabled}
            onChange={(e) => {
              const on = e.target.checked;
              setAutoPay((prev) => ({
                ...prev,
                enabled: on,
                ...(!on ? { exchange_enabled: false } : {}),
              }));
            }}
            color="success"
            sx={{ transform: "scale(1.3)", ml: 2 }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            mt: 2,
            borderRadius: 2,
            border: `2px solid ${autoPay.exchange_enabled ? "rgba(100,181,246,0.5)" : "rgba(255,255,255,0.12)"}`,
            bgcolor: autoPay.exchange_enabled ? "rgba(100,181,246,0.08)" : "rgba(255,255,255,0.03)",
            opacity: autoPay.enabled ? 1 : 0.55,
            transition: "all 0.2s",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
              Bybit on-chain payout (Phase 2) is{" "}
              <Box
                component="span"
                sx={{ color: autoPay.exchange_enabled ? "#64b5f6" : "#ff8a80" }}
              >
                {autoPay.exchange_enabled ? "ON" : "OFF"}
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ color: CAPTION_MUTED, mt: 0.5 }}>
              {autoPay.exchange_enabled
                ? "Eligible withdrawals are sent via Bybit API (bybit-relay). Client email only after on-chain confirmation."
                : "Phase 1 — auto-accept and mark ready-to-pay; admin pays on-chain manually."}
            </Typography>
            {!autoPay.enabled && (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", display: "block", mt: 0.5 }}>
                Turn on Auto-Pay above to enable this toggle.
              </Typography>
            )}
          </Box>
          <Switch
            checked={!!autoPay.exchange_enabled}
            onChange={(e) => updateAutoPay("exchange_enabled", e.target.checked)}
            disabled={!autoPay.enabled}
            color="info"
            sx={{ transform: "scale(1.3)", ml: 2 }}
          />
        </Box>

        {autoPay.enabled && !autoPay.exchange_enabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Auto-Pay is ON (Phase 1). Ensure liquidity wallets are funded and wallet security cooldowns are configured.
          </Alert>
        )}
        {autoPay.enabled && autoPay.exchange_enabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Phase 2 is ON — withdrawals will be sent automatically via Bybit. Ensure API key, address book and KYC whitelist are configured before saving.
          </Alert>
        )}
      </SectionCard>

      <SectionCard
        title="Amount Thresholds"
        description="Minimum and maximum amount per single auto-pay transaction and total daily cap."
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Min Amount per Withdrawal (USD)" type="number"
              value={autoPay.min_amount}
              onChange={(e) => updateAutoPay("min_amount", Number(e.target.value))}
              helperText="Withdrawals below this amount stay in the manual queue. Default $100."
              InputLabelProps={FIELD_LABEL_PROPS}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "#2af598", fontWeight: 700 }}>$</Typography>, sx: { color: "#fff" } }}
              FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Max Amount per Withdrawal (USD)" type="number"
              value={autoPay.max_amount}
              onChange={(e) => updateAutoPay("max_amount", Number(e.target.value))}
              helperText="Withdrawals above this amount will NOT be auto-processed — admin must approve manually"
              InputLabelProps={FIELD_LABEL_PROPS}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "#2af598", fontWeight: 700 }}>$</Typography>, sx: { color: "#fff" } }}
              FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Daily Cap (USD)" type="number"
              value={autoPay.daily_cap}
              onChange={(e) => updateAutoPay("daily_cap", Number(e.target.value))}
              helperText="Maximum total USD auto-paid in a 24h window. 0 = no cap."
              InputLabelProps={FIELD_LABEL_PROPS}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "#2af598", fontWeight: 700 }}>$</Typography>, sx: { color: "#fff" } }}
              FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Timing — Accept &amp; Pay Delays"
        description="How long to wait before auto-accepting a withdrawal and before marking it as paid."
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Accept Delay (minutes)" type="number"
              value={autoPay.accept_delay_minutes}
              onChange={(e) => updateAutoPay("accept_delay_minutes", Number(e.target.value))}
              helperText={`After client submits → auto-accept after this delay (currently ${Math.round(autoPay.accept_delay_minutes / 60)}h)`}
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Pay Delay (minutes)" type="number"
              value={autoPay.pay_delay_minutes}
              onChange={(e) => updateAutoPay("pay_delay_minutes", Number(e.target.value))}
              helperText={`After auto-accept → mark ready-to-pay after this delay (currently ${Math.round(autoPay.pay_delay_minutes / 60)}h)`}
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Max Window (hours)" type="number"
              value={autoPay.max_window_hours}
              onChange={(e) => updateAutoPay("max_window_hours", Number(e.target.value))}
              helperText="Transactions older than this are skipped by auto-pay"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>
        <Alert severity="info" sx={{ mt: 2, "& .MuiAlert-message": { color: "rgba(255,255,255,0.85)", width: "100%" } }}>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
            <strong>Total flow:</strong> Client submits → wait{" "}
            <Box component="span" sx={{ color: "#ffd54f", fontWeight: 700 }}>{Math.round(autoPay.accept_delay_minutes / 60)}h</Box>
            {" "}→ auto-accept → wait{" "}
            <Box component="span" sx={{ color: "#ffd54f", fontWeight: 700 }}>{Math.round(autoPay.pay_delay_minutes / 60)}h</Box>
            {" "}→ {autoPay.exchange_enabled ? (
              <>Bybit withdraw via API → poll confirmation → mark paid.</>
            ) : (
              <>mark ready-to-pay <Box component="span" sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>(on-chain payment: manual)</Box></>
            )}
          </Typography>
        </Alert>
      </SectionCard>

      <SectionCard
        title="Operating Window"
        description="When ON, auto-pay only accepts and pays within business hours. Outside the window, requests are deferred to the next opening (never cancelled). Off by default — no effect on the current flow."
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoPay.operating_window_enabled === true}
                onChange={(e) => updateAutoPay("operating_window_enabled", e.target.checked)}
                sx={{ "& .MuiSwitch-thumb": { bgcolor: autoPay.operating_window_enabled ? "#2af598" : undefined } }}
              />
            }
            label={
              <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                Enable operating window
              </Typography>
            }
          />
          <Chip
            size="small"
            label={autoPay.operating_window_enabled ? "ON — restricts hours" : "OFF — 24/7 (current behaviour)"}
            sx={{
              fontWeight: 700,
              bgcolor: autoPay.operating_window_enabled ? "rgba(42,245,152,0.15)" : "rgba(255,255,255,0.08)",
              color: autoPay.operating_window_enabled ? "#2af598" : "rgba(255,255,255,0.7)",
              border: `1px solid ${autoPay.operating_window_enabled ? "rgba(42,245,152,0.4)" : "rgba(255,255,255,0.2)"}`,
            }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Open hour (0–23)" type="number"
              value={autoPay.operating_start_hour}
              onChange={(e) => updateAutoPay("operating_start_hour", Number(e.target.value))}
              disabled={!autoPay.operating_window_enabled}
              helperText="Auto-pay starts accepting/paying at this hour"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0, max: 23 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Close hour (0–23)" type="number"
              value={autoPay.operating_end_hour}
              onChange={(e) => updateAutoPay("operating_end_hour", Number(e.target.value))}
              disabled={!autoPay.operating_window_enabled}
              helperText="Auto-pay stops at this hour (exclusive)"
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
              inputProps={{ min: 0, max: 23 }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth label="Timezone" type="text"
              value={autoPay.operating_timezone || "Europe/London"}
              helperText="DST-aware (BST/GMT). Deposit cutoff (UTC) is unaffected."
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={{ ...FIELD_INPUT_PROPS, readOnly: true }} FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600, mt: 3, mb: 1 }}>
          Example scenarios (computed from the current config)
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)", mb: 1.5 }}>
          Window {String(Number(autoPay.operating_start_hour ?? 9)).padStart(2, "0")}:00–
          {String(Number(autoPay.operating_end_hour ?? 21)).padStart(2, "0")}:00 ·
          accept +{Math.round((autoPay.accept_delay_minutes || 0) / 60)}h ·
          pay +{Math.round((autoPay.pay_delay_minutes || 0) / 60)}h.
          Amber = deferred to the next opening. Rounded to whole hours for illustration.
        </Typography>
        <TableContainer component={Paper} sx={{ bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Request", "Accept eligible", "Accepted", "Pay eligible", "Paid"].map((h) => (
                  <TableCell key={h} sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 700, borderColor: "rgba(255,255,255,0.08)" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const startHour = Number(autoPay.operating_start_hour ?? 9);
                const endHour = Number(autoPay.operating_end_hour ?? 21);
                const acceptDelayH = Math.max(0, Math.round((autoPay.accept_delay_minutes || 0) / 60));
                const payDelayH = Math.max(0, Math.round((autoPay.pay_delay_minutes || 0) / 60));
                const anchors = [8, 10, 16, 19, 22];
                const cellSx = { color: "#fff", borderColor: "rgba(255,255,255,0.08)" };
                const deferSx = { color: "#ffd54f", fontWeight: 700, borderColor: "rgba(255,255,255,0.08)" };
                return anchors.map((requestHour) => {
                  const s = computeWindowScenario({ requestHour, startHour, endHour, acceptDelayH, payDelayH });
                  return (
                    <TableRow key={requestHour}>
                      <TableCell sx={cellSx}>{fmtAbsHour(requestHour)}</TableCell>
                      <TableCell sx={cellSx}>{fmtAbsHour(s.acceptEligible)}</TableCell>
                      <TableCell sx={s.acceptDeferred ? deferSx : cellSx}>{fmtAbsHour(s.acceptActual)}</TableCell>
                      <TableCell sx={cellSx}>{fmtAbsHour(s.payEligible)}</TableCell>
                      <TableCell sx={s.payDeferred ? deferSx : cellSx}>{fmtAbsHour(s.payActual)}</TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <SectionCard
        title="Wallet Security Cooldowns"
        description="Automatic — no manual switches. These timers are enforced by the auto-pay Cloud Function."
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, bgcolor: "rgba(42,245,152,0.07)", border: "1px solid rgba(42,245,152,0.25)", borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#2af598" }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#2af598" }}>New wallet — creation cooldown</Typography>
              </Box>
              <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: "#fff" }}>24h</Typography>
              <Typography variant="body2" sx={{ color: CAPTION_MUTED, lineHeight: 1.5 }}>
                Triggered automatically on wallet <strong style={{ color: "#2af598" }}>creation</strong>.
                Auto-pay skips the wallet until <code>auto_pay_eligible_from</code> passes. No admin action needed.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper sx={{ p: 2, bgcolor: "rgba(255,193,7,0.08)", border: "1px solid rgba(255,193,7,0.3)", borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ffd54f" }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#ffd54f" }}>Edited wallet — auto-pay suspension</Typography>
              </Box>
              <Typography variant="h4" fontWeight={800} sx={{ my: 0.5, color: "#fff" }}>24h</Typography>
              <Typography variant="body2" sx={{ color: CAPTION_MUTED, lineHeight: 1.5 }}>
                Triggered automatically on wallet <strong style={{ color: "#ffd54f" }}>edit</strong>.
                Auto-pay blocked via <code>edit_suspended_until</code>. Admin email notification sent automatically.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard
        title="Supported Assets &amp; Networks"
        description="Auto-pay will only process withdrawals for these asset/network combinations."
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#fff" }}>Assets</Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
          {ASSET_BRAND.map(({ id, label, activeColor, activeBg, activeBorder, dot }) => {
            const active = (autoPay.supported_assets || []).includes(id);
            return (
              <Box
                key={id}
                onClick={() => toggleAutoPayAsset(id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.75, py: 0.9, borderRadius: 2, cursor: "pointer",
                  border: `${active ? "2px" : "1.5px"} solid ${activeBorder}`,
                  bgcolor: activeBg,
                  transition: "all 0.15s",
                  "&:hover": { filter: "brightness(1.3)" },
                  userSelect: "none",
                  boxShadow: active ? `0 0 12px ${activeBorder}` : "none",
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: activeColor, fontSize: 13 }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#fff" }}>Networks</Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
          {NETWORK_BRAND.map(({ id, label, activeColor, activeBg, activeBorder, dot }) => {
            const active = (autoPay.supported_networks || []).includes(id);
            return (
              <Box
                key={id}
                onClick={() => toggleAutoPayNetwork(id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.75,
                  px: 1.75, py: 0.9, borderRadius: 2, cursor: "pointer",
                  border: `${active ? "2px" : "1.5px"} solid ${activeBorder}`,
                  bgcolor: activeBg,
                  transition: "all 0.15s",
                  "&:hover": { filter: "brightness(1.3)" },
                  userSelect: "none",
                  boxShadow: active ? `0 0 12px ${activeBorder}` : "none",
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: activeColor, fontSize: 13 }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </SectionCard>

      <SectionCard
        title="KYC Whitelist (Phase 1 — Test Mode)"
        description="When non-empty, auto-pay only processes withdrawals for users in this list. Use during controlled testing. Leave empty to apply auto-pay to all KYC-approved users (Phase 2+)."
      >
        <TextField
          fullWidth multiline minRows={2}
          label="Allowed user UIDs (one per line or comma-separated)"
          value={(autoPay.kyc_whitelist_uids || []).join("\n")}
          onChange={(e) => updateAutoPay(
            "kyc_whitelist_uids",
            e.target.value.split(/[\n,]+/).map((u) => u.trim()).filter(Boolean)
          )}
          helperText="Example: founder UID for the Phase 1 test. Remove all UIDs to open auto-pay to all users (Phase 2+)."
          InputLabelProps={FIELD_LABEL_PROPS}
          InputProps={{ sx: { fontFamily: "monospace", color: "#fff" } }}
          FormHelperTextProps={FIELD_HELPER_PROPS}
        />
      </SectionCard>

      <SectionCard
        title="Liquidity Wallets (Admin-side)"
        description="Fiddux operational wallets used to fund auto-pay withdrawals. These are YOUR company wallets — not client wallets. One entry per enabled asset × network pair."
      >
        <Grid container spacing={2}>
          {buildLiquidityWalletKeys(autoPay).map((key) => {
            const [asset, network] = key.split("_");
            return (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={`${asset} / ${network} liquidity wallet`}
                  size="small"
                  value={autoPay.liquidity_wallets?.[key] || ""}
                  onChange={(e) => updateAutoPayLiquidityWallet(key, e.target.value)}
                  helperText={`Required when ${asset} and ${network} are enabled. Payout uses Bybit Funding; this flag enables auto-pay for the pair.`}
                  InputLabelProps={FIELD_LABEL_PROPS}
                  InputProps={{ sx: { fontFamily: "monospace", color: "#fff" } }}
                  FormHelperTextProps={FIELD_HELPER_PROPS}
                />
              </Grid>
            );
          })}
          {buildLiquidityWalletKeys(autoPay).length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
                Enable at least one asset (USDT/USDC) and one network above to configure liquidity wallets.
              </Typography>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 1, mb: 4 }}>
        <Button
          variant="outlined"
          onClick={() => { setAutoPay({ ...originalAutoPay }); }}
          disabled={!autoPayHasChanges || autoPaySaving}
          sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.3)", "&:hover": { borderColor: "rgba(255,255,255,0.6)" } }}
        >
          Discard Auto-Pay Changes
        </Button>
        <Button
          variant="contained"
          startIcon={autoPaySaving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={saveAutoPaySettings}
          disabled={!autoPayHasChanges || autoPaySaving}
          sx={{ bgcolor: "#2af598", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#1de585" }, "&.Mui-disabled": { bgcolor: "rgba(42,245,152,0.25)", color: "rgba(0,0,0,0.5)" } }}
        >
          {autoPaySaving ? "Saving…" : "Save Auto-Pay Settings"}
        </Button>
      </Box>
    </>
  );

  // ==================== TAB 5: Monitoring ====================
  const CRON_INTERVAL_OPTIONS = [2, 5, 15, 30, 60, 120];

  const renderMonitoring = () => (
    <>
      <SectionCard title="Scheduled Jobs" description="Controla a frequência com que o sistema verifica emails e reminders agendados.">
        <FormControl fullWidth sx={{ maxWidth: 320 }}>
          <InputLabel>Intervalo do Cron de Agendamentos (minutos)</InputLabel>
          <Select
            value={settings.scheduled_cron_interval_minutes ?? 120}
            label="Intervalo do Cron de Agendamentos (minutos)"
            onChange={(e) => updateSetting("scheduled_cron_interval_minutes", Number(e.target.value))}
          >
            {CRON_INTERVAL_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>{m} min</MenuItem>
            ))}
          </Select>
          <Typography variant="caption" sx={{ color: CAPTION_MUTED, mt: 1, display: "block" }}>
            2 min para testes, 120 min para produção.
          </Typography>
        </FormControl>
      </SectionCard>

      <SectionCard title="Function Monitoring" description="Configure automated monitoring of Cloud Functions.">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Monitoring Window (hours)" type="number"
              value={settings.monitoring_window_hours}
              onChange={(e) => updateSetting("monitoring_window_hours", Number(e.target.value))}
              helperText="How many hours of logs to check for errors"
              inputProps={{ min: 1, max: 24 }}
              InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS} FormHelperTextProps={FIELD_HELPER_PROPS}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Functions to Monitor" description="Cloud Functions that are checked for failures.">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {(settings.functions_to_monitor || []).map((fn, i) => (
            <Chip key={i} label={fn} onDelete={() => removeFromList("functions_to_monitor", i)} size="small" color="primary" variant="outlined" />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder="functionName" value={newFunction} onChange={(e) => setNewFunction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addToList("functions_to_monitor", newFunction, setNewFunction)}
            sx={{ minWidth: 280 }}
            InputLabelProps={FIELD_LABEL_PROPS} InputProps={FIELD_INPUT_PROPS}
          />
          <IconButton size="small" onClick={() => addToList("functions_to_monitor", newFunction, setNewFunction)} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </SectionCard>
    </>
  );

  // ==================== Main Render ====================
  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 900,
        mx: "auto",
        "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.55)" },
        "& .MuiInputLabel-root": { color: CAPTION_MUTED },
        "& .MuiInputLabel-root.Mui-focused": { color: "#2af598" },
        "& .MuiInputBase-input": { color: "#fff" },
        "& .MuiFormControlLabel-label": { color: "#fff" },
        "& .MuiSelect-select": { color: "#fff" },
        "& .MuiTab-root": { color: CAPTION_MUTED },
        "& .MuiTab-root.Mui-selected": { color: "#2af598" },
        "& .MuiTypography-subtitle2": { color: "#fff" },
        "& code": { color: "rgba(255,255,255,0.85)", bgcolor: "rgba(255,255,255,0.08)", px: 0.5, borderRadius: 0.5 },
      }}
    >
      <LinkGoogleCard />
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ p: 1.25, bgcolor: "rgba(42,245,152,0.12)", borderRadius: 2, display: "flex" }}>
            <SettingsIcon sx={{ fontSize: 28, color: "#2af598" }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>Admin Settings</Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25, fontSize: 13 }}>
              Emails · Alert thresholds · Branding ·{" "}
              <Box component="span" sx={{ color: "#2af598" }}>Auto-Pay</Box>
              {" "}· Monitoring
            </Typography>
          </Box>
        </Box>
        {lastUpdated && (
          <Tooltip title={`Updated by ${lastUpdatedBy || "unknown"}`}>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)", display: "block" }}>
                Last saved: {lastUpdated.toLocaleString()}
              </Typography>
              {lastUpdatedBy && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", display: "block" }}>
                  by {lastUpdatedBy}
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
      </Box>

      {isSandbox && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Sandbox environment - Changes here only affect the Sandbox project.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<EmailIcon />} label="Email Controls" iconPosition="start" />
          <Tab icon={<NotificationsActiveIcon />} label="Alert Thresholds" iconPosition="start" />
          <Tab icon={<BrushIcon />} label="Branding" iconPosition="start" />
          <Tab icon={<AutorenewIcon />} label="Auto-Pay Withdrawals" iconPosition="start" />
          <Tab icon={<MonitorHeartIcon />} label="Monitoring" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabIndex} index={0}>{renderEmailControls()}</TabPanel>
      <TabPanel value={tabIndex} index={1}>{renderAlertThresholds()}</TabPanel>
      <TabPanel value={tabIndex} index={2}>{renderBranding()}</TabPanel>
      <TabPanel value={tabIndex} index={3}>{renderAutomations()}</TabPanel>
      <TabPanel value={tabIndex} index={4}>{renderMonitoring()}</TabPanel>

      {/* Global Action Buttons — hidden on the Auto-Pay tab (has its own save flow) */}
      {tabIndex !== 3 && (
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2, mb: 4, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RestoreIcon />}
            onClick={handleResetToDefaults}
            disabled={isSaving}
            sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.3)", "&:hover": { borderColor: "rgba(255,255,255,0.6)" } }}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.3)", "&:hover": { borderColor: "rgba(255,255,255,0.6)" } }}
          >
            Discard Changes
          </Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            sx={{ bgcolor: "#2af598", color: "#000", fontWeight: 700, "&:hover": { bgcolor: "#1de585" }, "&.Mui-disabled": { bgcolor: "rgba(42,245,152,0.25)", color: "rgba(0,0,0,0.5)" } }}
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </Box>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to save these admin settings? Changes will take effect within 5 minutes (Cloud Functions cache).
          </DialogContentText>
          {settings.testing_mode && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Testing Mode is ON. Only whitelisted emails will receive messages.
            </Alert>
          )}
          {!settings.emails_enabled && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Client emails are DISABLED. No emails will be sent to clients.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmSave} variant="contained" autoFocus>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSettings;
